import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps } from 'react';
import { TabsContent } from '../../../../design-system/tabs/tabs-primitives';
import { InvoiceEvidenceView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { InvoiceDrawerContentBodyVariant } from '../invoice-drawer/invoice-drawer-content-body';
import { InvoicesSectionTabs, TabTrigger, TabValue } from '../invoices-section-tabs/invoices-section-tabs';
import { InvoiceTabContentWrapper } from './invoice-tab-content-wrapper';
import { PaidInvoiceTable } from './paid-invoice-table';
import { ReviewMatchesTableWrapper } from './review-matches-tab/review-matches-table-wrapper';
import { ToPayInvoiceTable } from './to-pay-invoice-table';

type EvidenceDataConfig = {
  unpaidInvoices: InvoiceEvidenceView[];
  inReviewInvoices: InvoiceEvidenceView[];
  completedInvoices: InvoiceEvidenceView[];
};

type InvoiceDataSectionProps = {
  evidenceDataConfig: EvidenceDataConfig;
  isEmailConnected: boolean;
  isFetchingDocumentUrl: boolean;
  selectedEvidenceId?: string;
  approvingEvidenceId?: string[];
  onConnectGmail: () => void;
  onReviewInvoice: (invoiceId: string, variant: InvoiceDrawerContentBodyVariant) => void;
  onSelectMenuItem: ComponentProps<typeof ToPayInvoiceTable>['onSelectMenuItem'];
  onDataSorted: (sortedEvidence: InvoiceEvidenceView[]) => void;
  onApproveInvoice: (invoiceIds: string[]) => Promise<void>;
  onReviewTransaction: (transactionId: string) => void;
};

export const InvoiceDataSection = ({
  evidenceDataConfig,
  isEmailConnected,
  isFetchingDocumentUrl,
  selectedEvidenceId,
  approvingEvidenceId,
  onConnectGmail,
  onReviewInvoice,
  onSelectMenuItem,
  onDataSorted,
  onApproveInvoice,
  onReviewTransaction,
}: InvoiceDataSectionProps) => {
  const { isReviewMatchLive } = useFlags();
  const { unpaidInvoices, inReviewInvoices, completedInvoices } = evidenceDataConfig;
  const hasInvoiceData = unpaidInvoices.length > 0 || inReviewInvoices.length > 0 || completedInvoices.length > 0;

  const triggers = getInvoiceSectionTabsTriggers({
    isEmailConnected,
    unpaidInvoicesCount: unpaidInvoices.length,
    inReviewInvoicesCount: inReviewInvoices.length,
    completedInvoicesCount: completedInvoices.length,
    isReviewMatchLive,
  });
  const tabsContentClassName = 'relative rounded-none';

  return (
    <div className="bg-white">
      <InvoicesSectionTabs triggers={triggers}>
        {triggers.map((t) => {
          const tabInvoiceCount = getTabInvoiceCount({
            tabValue: t.value,
            evidenceCountConfig: {
              unpaid: unpaidInvoices.length,
              inReview: inReviewInvoices.length,
              paid: completedInvoices.length,
            },
          });

          return (
            <TabsContent key={t.value} value={t.value} className={tabsContentClassName}>
              <InvoiceTabContentWrapper
                variant={t.value}
                tabInvoiceCount={tabInvoiceCount}
                hasInvoiceData={hasInvoiceData}
                isEmailConnected={isEmailConnected}
                onConnectGmail={onConnectGmail}
              >
                {t.value === 'TO_PAY' && (
                  <ToPayInvoiceTable
                    evidences={unpaidInvoices}
                    isFetchingDocumentUrl={isFetchingDocumentUrl}
                    selectedEvidenceId={selectedEvidenceId}
                    isEmailConnected={isEmailConnected}
                    onReviewInvoice={(invoiceId) => onReviewInvoice(invoiceId, 'TO_PAY')}
                    onSelectMenuItem={onSelectMenuItem}
                    onDataSorted={onDataSorted}
                  />
                )}

                {t.value === 'REVIEW_MATCHES' && (
                  <ReviewMatchesTableWrapper
                    evidences={inReviewInvoices}
                    approvingEvidenceId={approvingEvidenceId}
                    onSelectMenuItem={onSelectMenuItem}
                    onReviewInvoice={(invoiceId) => onReviewInvoice(invoiceId, 'REVIEW_MATCHES')}
                    onApproveInvoice={onApproveInvoice}
                    onDataSorted={onDataSorted}
                  />
                )}

                {t.value === 'COMPLETED' && (
                  <PaidInvoiceTable
                    evidences={completedInvoices}
                    onSelectMenuItem={onSelectMenuItem}
                    onReviewTransaction={onReviewTransaction}
                    onDataSorted={onDataSorted}
                  />
                )}
              </InvoiceTabContentWrapper>
            </TabsContent>
          );
        })}
      </InvoicesSectionTabs>
    </div>
  );
};

type GetInvoiceSectionTabsTriggersArgs = {
  isEmailConnected: boolean;
  unpaidInvoicesCount?: number;
  inReviewInvoicesCount?: number;
  completedInvoicesCount?: number;
  isReviewMatchLive: boolean;
};

const getInvoiceSectionTabsTriggers = ({
  isEmailConnected,
  unpaidInvoicesCount,
  inReviewInvoicesCount,
  completedInvoicesCount,
  isReviewMatchLive,
}: GetInvoiceSectionTabsTriggersArgs): TabTrigger[] => {
  const shouldShowToPayCounter = isEmailConnected && !!unpaidInvoicesCount && unpaidInvoicesCount > 0;
  const shouldShowInReviewCounter = isEmailConnected && !!inReviewInvoicesCount && inReviewInvoicesCount > 0;
  const shouldShowCompletedCounter = isEmailConnected && !!completedInvoicesCount && completedInvoicesCount > 0;

  const toPayCounterConfig = shouldShowToPayCounter
    ? { showCounter: true as const, count: unpaidInvoicesCount }
    : { showCounter: false as const };
  const inReviewCounterConfig = shouldShowInReviewCounter
    ? { showCounter: true as const, count: inReviewInvoicesCount }
    : { showCounter: false as const };
  const completedCounterConfig = shouldShowCompletedCounter
    ? { showCounter: true as const, count: completedInvoicesCount }
    : { showCounter: false as const };

  return [
    {
      value: 'TO_PAY',
      counterConfig: toPayCounterConfig,
    },
    ...(isReviewMatchLive
      ? [
          {
            value: 'REVIEW_MATCHES' as const,
            counterConfig: inReviewCounterConfig,
          },
        ]
      : []),
    {
      value: 'COMPLETED',
      counterConfig: completedCounterConfig,
    },
  ];
};

type GetTabInvoiceCountArgs = {
  tabValue: TabValue;
  evidenceCountConfig: {
    unpaid: number;
    inReview: number;
    paid: number;
  };
};

const getTabInvoiceCount = ({ tabValue, evidenceCountConfig }: GetTabInvoiceCountArgs) => {
  switch (tabValue) {
    case 'TO_PAY':
      return evidenceCountConfig.unpaid;
    case 'REVIEW_MATCHES':
      return evidenceCountConfig.inReview;
    case 'COMPLETED':
      return evidenceCountConfig.paid;
    default:
      return exhaustiveCheck(tabValue);
  }
};
